import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Space,
  Descriptions,
  Tag,
  Spin,
  message,
  Row,
  Col,
  Divider,
  List,
  Avatar,
} from 'antd';
import {
  ArrowLeftOutlined,
  EditOutlined,
  DeleteOutlined,
  UserOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import { OperatorGroup } from '../../types/operatorGroup';
import operatorGroupService from '../../services/operatorGroupService';
import { useErrorHandling } from '../../hooks/useErrorHandling';

const OperatorGroupDetail: React.FC = () => {
  const navigate = useNavigate();
  const { code } = useParams<{ code: string }>();
  const { t } = useTranslation();
  const { showError } = useErrorHandling();
  
  // 状态管理
  const [loading, setLoading] = useState(true);
  const [operatorGroup, setOperatorGroup] = useState<OperatorGroup | null>(null);

  // 初始化加载
  useEffect(() => {
    if (code) {
      loadOperatorGroupDetail();
    } else {
      message.error(t('operatorGroup.messages.codeCannotBeEmpty'));
      navigate('/operator-group');
    }
  }, [code]);

  // 加载操作组详情
  const loadOperatorGroupDetail = async () => {
    if (!code) return;
    
    setLoading(true);
    try {
      const response = await operatorGroupService.getOperatorGroupByCode(code);
      
      if (response.success && response.data) {
        setOperatorGroup(response.data);
      } else {
        showError(t('operatorGroup.messages.loadDetailFailed'));
        navigate('/operator-group');
      }
    } catch (error) {
      console.error('加载操作组详情失败:', error);
      message.error(t('operatorGroup.messages.loadDetailFailed'));
      navigate('/operator-group');
    } finally {
      setLoading(false);
    }
  };

  // 返回列表
  const handleBack = () => {
    navigate('/operator-group');
  };

  // 编辑操作组
  const handleEdit = () => {
    if (operatorGroup) {
      navigate(`/operator-group/edit/${operatorGroup.code}`);
    }
  };

  // 删除操作组
  const handleDelete = () => {
    if (!operatorGroup) return;
    
    // 这里可以添加删除确认逻辑
    message.info(t('operatorGroup.messages.deleteFromList'));
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!operatorGroup) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>{t('operatorGroup.messages.operatorGroupNotExists')}</p>
          <Button type="primary" onClick={handleBack}>
            {t('operatorGroup.messages.backToList')}
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <div>
      {/* 页面头部 */}
      <Card className="header-card">
        <Row justify="space-between" align="middle">
          <Col>
            <Space>
              <Button
                type="text"
                icon={<ArrowLeftOutlined />}
                onClick={handleBack}
              >
                {t('operatorGroup.back')}
              </Button>
              <Divider type="vertical" />
              <h2 style={{ margin: 0 }}>{t('operatorGroup.detailTitle')}</h2>
            </Space>
          </Col>
          <Col>
            <Space>
              <Button
                type="primary"
                icon={<EditOutlined />}
                onClick={handleEdit}
              >
                {t('operatorGroup.edit')}
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      <div style={{ marginTop: '16px' }}>
        <Row gutter={[16, 16]}>
          {/* 基本信息 */}
          <Col xs={24} lg={16}>
            <Card title={t('operatorGroup.detail.basicInfo')} className="info-card">
              <Descriptions column={2} bordered>
                <Descriptions.Item label={t('operatorGroup.code')} span={1}>
                  <Tag color="blue">{operatorGroup.code}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label={t('operatorGroup.name')} span={1}>
                  {operatorGroup.name}
                </Descriptions.Item>
                <Descriptions.Item label={t('operatorGroup.divisionCode')} span={1}>
                  {operatorGroup.responsibleTeam}
                </Descriptions.Item>
                <Descriptions.Item label={t('operatorGroup.divisionName')} span={1}>
                  <Tag color="green">{operatorGroup.responsibleTeam}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label={t('operatorGroup.director')} span={2}>
                  <Space>
                    <UserOutlined />
                    {operatorGroup.workers.find(w => w.accountId === operatorGroup.director)?.name || operatorGroup.director}
                    <Tag color="orange">{t('operatorGroup.director')}</Tag>
                  </Space>
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>

          {/* 统计信息 */}
          <Col xs={24} lg={8}>
            <Card title={t('operatorGroup.detail.statisticsInfo')} className="stats-card">
              <div style={{ textAlign: 'center' }}>
                <div style={{ marginBottom: '16px' }}>
                  <TeamOutlined style={{ fontSize: '24px', color: '#1890ff' }} />
                  <div style={{ marginTop: '8px' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1890ff' }}>
                      {operatorGroup.workers.length}
                    </div>
                    <div style={{ color: '#666' }}>{t('operatorGroup.workersTotal')}</div>
                  </div>
                </div>
              </div>
            </Card>
          </Col>

          {/* 操作员列表 */}
          <Col xs={24}>
            <Card title={t('operatorGroup.detail.workersList', { count: operatorGroup.workers.length })} className="workers-card">
              <List
                grid={{ 
                  gutter: 16, 
                  xs: 1, 
                  sm: 2, 
                  md: 3, 
                  lg: 4, 
                  xl: 6, 
                  xxl: 8 
                }}
                dataSource={operatorGroup.workers}
                renderItem={(worker) => (
                  <List.Item>
                    <Card size="small" className="worker-card">
                      <div style={{ textAlign: 'center' }}>
                        <Avatar
                          size={48}
                          icon={<UserOutlined />}
                          style={{ 
                            backgroundColor: worker.accountId === operatorGroup.director ? '#ff7a45' : '#1890ff',
                            marginBottom: '8px'
                          }}
                        />
                        <div style={{ fontWeight: 'bold' }}>{worker.name}</div>
                        <div style={{ color: '#666', fontSize: '12px' }}>{worker.accountId}</div>
                        {worker.accountId === operatorGroup.director && (
                          <Tag color="orange" style={{ marginTop: '4px' }}>
                            {t('operatorGroup.director')}
                          </Tag>
                        )}
                      </div>
                    </Card>
                  </List.Item>
                )}
              />
            </Card>
          </Col>

          {/* 系统信息 */}
          <Col xs={24}>
            <Card title={t('operatorGroup.systemInfo')} className="system-info-card">
              <Descriptions column={2} bordered>
                <Descriptions.Item label={t('operatorGroup.creatorAccount')} span={1}>
                  {operatorGroup.creatorAccount}
                </Descriptions.Item>
                <Descriptions.Item label={t('operatorGroup.creatorName')} span={1}>
                  {operatorGroup.creatorName}
                </Descriptions.Item>
                <Descriptions.Item label={t('operatorGroup.createTime')} span={1}>
                  {dayjs(operatorGroup.gmtCreate).format('YYYY-MM-DD HH:mm:ss')}
                </Descriptions.Item>
                <Descriptions.Item label={t('operatorGroup.gmtModified')} span={1}>
                  {dayjs(operatorGroup.gmtModified).format('YYYY-MM-DD HH:mm:ss')}
                </Descriptions.Item>
                <Descriptions.Item label={t('operatorGroup.modifierAccount')} span={1}>
                  {operatorGroup.modifierAccount}
                </Descriptions.Item>
                <Descriptions.Item label={t('operatorGroup.modifierName')} span={1}>
                  {operatorGroup.modifierName}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default OperatorGroupDetail;